// EXEMPT
import * as z from "zod";

import type {
  ModelDefinition,
  ModelObject,
  ModelValue,
} from "@/__main__/data-model.mjs";
import {
  __INTERNAL_IS_MAP_SYMBOL as isMap,
  arbitraryKeys,
  isMappedModel,
  isUnionModel,
} from "@/__main__/data-model.mjs";
import symbolName from "@/util/symbol-name.mjs";
import { extractLiterals } from "@/util/zod-util.mjs";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapZodGqlValue(model: z.core.$ZodType<any>) {
  if (model instanceof z.ZodArray) {
    return mapZodGqlValue(model.element);
  }

  if (model instanceof z.ZodObject) {
    return Object.entries(model.shape)
      .map(([key, value]) => mapZodGqlEntry(key, value))
      .join("\n");
  }

  if (model instanceof z.ZodRecord) {
    const keys = model.keyType;
    if (keys instanceof z.ZodUnion || keys instanceof z.ZodLiteral) {
      return extractLiterals(keys)
        .map((key) => mapZodGqlEntry(key.toString(), model.valueType))
        .join("\n");
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapZodGqlEntry(key: string, value: z.core.$ZodType<any>) {
  if (value instanceof z.ZodArray) value = value.element;

  if (value instanceof z.ZodObject || value instanceof z.ZodRecord) {
    const mappedValue = mapZodGqlValue(value);

    return `${key} {
      ${mappedValue}
    }`;
  }

  return key;
}

function mapGqlValue(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: ModelValue | z.core.$ZodType<any>,
  param?: object | null | undefined,
): string | null {
  if (typeof model === "string") {
    return model;
  } else if (model instanceof z.core.$ZodType) {
    return mapZodGqlValue(model);
  } else if (typeof model !== "object") {
    return null;
  }

  if (Array.isArray(model)) {
    if (model.length === 1) {
      const modelValue = model[0];

      if (typeof modelValue === "object") {
        return mapGqlValue(modelValue, param);
      }
    }

    return null;
  }

  if (isMappedModel(model)) {
    const modelKey = model[isMap].key;
    const modelValue = model[isMap].value;

    if (typeof modelKey === "object" && Array.isArray(modelKey)) {
      if (
        isUnionModel(modelKey) &&
        modelKey.every((key) => typeof key === "string")
      ) {
        // The key is a known union of strings, so expand it in the query
        return modelKey
          .map((key: string) => mapGqlEntry(key, modelValue, param?.[key]))
          .join("\n");
      }
    }

    return null;
  }

  if (model[arbitraryKeys]) {
    return null;
  }

  return Object.entries(model)
    .map(([key, value]) => mapGqlEntry(key, value, param?.[key]))
    .join("\n");
}

function mapGqlEntry(
  key: string,
  value: ModelValue,
  param: string | object | undefined,
): string {
  if (typeof value === "object") {
    const result = mapGqlValue(
      value,
      typeof param === "string" ? undefined : param,
    );

    if (result) {
      return `${key} ${typeof param === "string" ? param + " " : ""}{
        ${result}
      }`;
    }
  }

  return key;
}

export const queries = new Set();

const GQL_PARAMS: unique symbol = symbolName("gql-params");

type GqlParamObject<M extends ModelDefinition = ModelDefinition> =
  M extends object
    ? {
        [K in keyof M]?: M[K] extends infer O extends ModelObject
          ? OmitEmpty<OmitNever<GqlParamObject<O>>> | string
          : M[K] extends readonly [infer O extends ModelObject]
            ? OmitEmpty<OmitNever<GqlParamObject<O>>> | string
            : never;
      }
    : never;

type GqlParam<
  M extends ModelDefinition = ModelDefinition,
  O extends object = object,
> = { [GQL_PARAMS]: [M, O] };

/**
 * Allows inserting parameters into the GQL query string.
 *
 * For example, given the following GQL query:
 *
 * ```gql
 * query LeagueChampionInsights($language: String) {
 *   championInsights {
 *     tips {
 *       weaknesses {
 *         localizedInsights {
 *           language
 *           translation
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * If you wanted to query `localizedInsights` with the `language` parameter, your parameter object
 * would be:
 *
 * ```js
 * {
 *   championInsights: {
 *     tips: {
 *       weaknesses: {
 *         localizedInsights: "(language: $language)"
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * Which would result in the following query:
 *
 * ```gql
 * query LeagueChampionInsights($language: String) {
 *   championInsights {
 *     tips {
 *       weaknesses {
 *         localizedInsights(language: $language) {
 *           language
 *           translation
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 */
export function gqlParams<M extends ModelDefinition>(
  model: M,
  params: GqlParamObject<M>,
): GqlParam<M, typeof params> {
  return { [GQL_PARAMS]: [model, params] };
}

/**
 * Constructs a GQL query string from a template string using embedded model
 * definitions. To parameterize the query, use the `gqlParams` function.
 */
export default function gql(
  strings: TemplateStringsArray,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...models: (string | ModelDefinition | z.Schema<any> | GqlParam)[]
): string {
  const parts = [];
  for (const str of strings) {
    parts.push(str);

    const model = models.shift();

    if (model) {
      if (model[GQL_PARAMS]) {
        parts.push(mapGqlValue(model[GQL_PARAMS][0], model[GQL_PARAMS][1]));
      } else {
        parts.push(mapGqlValue(model));
      }
    }
  }

  const text = parts.join("");

  // https://github.com/jeromecovington/graphql-compress/blob/master/index.js
  const query = text.replace(/\s+/g, " ");
  const result = query.replace(/\s*(\[|\]|\{|\}|\(|\)|:|\,)\s*/g, "$1").trim(); // eslint-disable-line no-useless-escape

  queries.add(result.match(/^(?:.*)[a-zA-Z]/)?.[0]);

  return result;
}
